import { inject, Injectable } from '@angular/core';
import { GrowlerService } from '@app/components/core/growler/service/growler.service';
import { GrowlerPendingService } from '@app/service/base-growler/growler-pending.service';
import { angularjs } from '@app/service/migration-helpers/angularjs.injection';
import type { IContentStore } from '@app/interface/content/content-store.interface';
import { GROWLER_KEYS } from '@app/service/add-to-cart-growler/growler-content-keys';

export interface CartChangeFlags {
  hasPricingUpdate: boolean;
  hasUnavailableSelections: boolean;
  hasBackgroundUpdate: boolean;
}

@Injectable({ providedIn: 'root' })
export class CartChangeGrowlerService extends GrowlerPendingService<CartChangeFlags> {
  private readonly growlerService = inject(GrowlerService);
  private readonly contentStore = inject<IContentStore>(angularjs.ContentStore);
  private _hasBeenShown = false;

  /** Returns true when the change growler has not yet been shown for the current cart session. */
  hasNotBeenShown(): boolean {
    return !this._hasBeenShown;
  }

  /** Mark the change growler as shown for the current cart session. */
  markShown(): void {
    this._hasBeenShown = true;
  }

  /** Reset shown state — call on route change or controller destroy. */
  reset(): void {
    this._hasBeenShown = false;
  }

  /**
   * Show one top-of-cart growler per applicable flag (queue).
   * Call after repriceCart() resolves with change flags.
   */
  showCartAlertGrowlers(flags: CartChangeFlags): void {
    if (!flags.hasPricingUpdate && !flags.hasUnavailableSelections && !flags.hasBackgroundUpdate) return;

    if (flags.hasUnavailableSelections) {
      this.growlerService.info({
        title: this.contentStore.translate(GROWLER_KEYS.SELECTIONS_UNAVAILABLE_TITLE),
        message: this.contentStore.translate(GROWLER_KEYS.SELECTIONS_UNAVAILABLE_MESSAGE),
        closeable: true,
        growlerStyle: 'floating'
      });
    }

    if (flags.hasBackgroundUpdate) {
      this.growlerService.info({
        title: this.contentStore.translate(GROWLER_KEYS.SELECTIONS_UNAVAILABLE_TITLE),
        message: this.contentStore.translate(GROWLER_KEYS.SELECTIONS_UNAVAILABLE_MESSAGE),
        closeable: true,
        growlerStyle: 'floating'
      });
    }

    if (flags.hasPricingUpdate) {
      this.growlerService.info({
        title: this.contentStore.translate(GROWLER_KEYS.PRICES_UPDATED_TITLE),
        message: this.contentStore.translate(GROWLER_KEYS.PRICES_UPDATED_MESSAGE),
        closeable: true,
        growlerStyle: 'floating'
      });
    }
  }

  /**
   * Defer change growler until cart page arrival (in-memory only; for copy flows triggered off-cart).
   * No-op when all flags are false.
   */
  setPending(flags: CartChangeFlags): void {
    if (!flags.hasPricingUpdate && !flags.hasUnavailableSelections && !flags.hasBackgroundUpdate) return;
    this.setPendingData(flags);
  }

  protected showPending(flags: CartChangeFlags): void {
    this.showCartAlertGrowlers(flags);
  }
}
